/** Stacked toast notifications (messages, achievements, gifts) — top dismisses, others move up. */

export type NotificationToastKind = "message" | "achievement" | "gift";

const TITLES: Record<NotificationToastKind, string> = {
    message: "Message notification",
    achievement: "Achievement unlocked",
    gift: "Gift sent",
};

const DURATIONS_MS: Record<NotificationToastKind, number> = {
    message: 5200,
    achievement: 5000,
    gift: 4800,
};

/** Matches `.notificationToast` slide transition. */
const TRANSITION_MS = 520;
const FLIP_MOVE_MS = 320;

type StackEntry = {
    id: number;
    el: HTMLElement;
    kind: NotificationToastKind;
    dismissTimer: ReturnType<typeof setTimeout> | null;
};

let container: HTMLElement | null = null;
let nextId = 0;
const entries: StackEntry[] = [];

function ensureContainer(): HTMLElement | null {
    if (!container) container = document.getElementById("notificationStack");
    return container;
}

function animateStackReflow(exclude?: HTMLElement): void {
    const stack = ensureContainer();
    if (!stack) return;
    for (const child of stack.children) {
        if (!(child instanceof HTMLElement) || child === exclude) continue;
        const beforeTop = child.dataset.flipTop;
        if (!beforeTop) continue;
        const delta = Number(beforeTop) - child.getBoundingClientRect().top;
        delete child.dataset.flipTop;
        if (Math.abs(delta) < 0.5) continue;
        child.style.transition = "none";
        child.style.transform = `translateX(0) translateY(${delta}px)`;
        requestAnimationFrame(() => {
            child.style.transition = `transform ${FLIP_MOVE_MS}ms ease`;
            child.style.transform = "translateX(0) translateY(0)";
            window.setTimeout(() => {
                if (child.isConnected) child.style.transform = "";
            }, FLIP_MOVE_MS + 40);
        });
    }
}

function snapshotStackTops(): void {
    const stack = ensureContainer();
    if (!stack) return;
    for (const child of stack.children) {
        if (child instanceof HTMLElement) {
            child.dataset.flipTop = String(child.getBoundingClientRect().top);
        }
    }
}

function removeEntryAt(idx: number, animateOut: boolean): void {
    const entry = entries[idx];
    if (!entry) return;
    if (entry.dismissTimer) {
        clearTimeout(entry.dismissTimer);
        entry.dismissTimer = null;
    }

    if (!entry.el.isConnected) {
        entries.splice(idx, 1);
        return;
    }

    if (!animateOut) {
        entry.el.remove();
        entries.splice(idx, 1);
        return;
    }

    snapshotStackTops();
    entry.el.classList.remove("notificationToast--show");
    window.setTimeout(() => {
        if (entry.el.isConnected) entry.el.remove();
        const removeIdx = entries.findIndex((e) => e.id === entry.id);
        if (removeIdx >= 0) entries.splice(removeIdx, 1);
        animateStackReflow();
    }, TRANSITION_MS);
}

function dismissNotificationToast(id: number): void {
    const idx = entries.findIndex((e) => e.id === id);
    if (idx < 0) return;
    removeEntryAt(idx, true);
}

export function pushNotificationToast(kind: NotificationToastKind, body: string): void {
    const stack = ensureContainer();
    const text = body.trim();
    if (!stack || !text) return;

    const id = ++nextId;
    const el = document.createElement("div");
    el.className = "notificationToast";
    el.dataset.notificationId = String(id);
    el.dataset.notificationKind = kind;

    const inner = document.createElement("div");
    inner.className = "notificationToastInner";

    const title = document.createElement("div");
    title.className = "notificationToastTitle";
    title.textContent = TITLES[kind];

    const bodyEl = document.createElement("div");
    bodyEl.className = "notificationToastBody";
    bodyEl.textContent = text;

    inner.append(title, bodyEl);
    el.append(inner);
    stack.appendChild(el);

    const entry: StackEntry = { id, el, kind, dismissTimer: null };
    entries.push(entry);

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            if (el.isConnected) el.classList.add("notificationToast--show");
        });
    });

    entry.dismissTimer = window.setTimeout(() => {
        dismissNotificationToast(id);
    }, DURATIONS_MS[kind]);
}

export function clearNotificationToastsByKind(kind: NotificationToastKind, immediate = false): void {
    for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].kind !== kind) continue;
        removeEntryAt(i, !immediate);
    }
}
